// Helper di visualizzazione del cliente.
//
// I dati azienda possono stare nei campi nativi (ragioneSociale, partitaIva, ...)
// oppure, per i clienti più vecchi, nei campi "per conto di terzi" o persino in
// `nome`. Le funzioni leggono in ordine: nativo, per-terzi, nome.
// Usarle ovunque si mostri o stampi un cliente; in scrittura usare i campi nativi.

export const getClientDisplayName = (c: any): string => {
    if (!c) return '';
    if (c.tipo === 'Azienda') {
        const fromNative = (c.ragioneSociale || '').trim();
        if (fromNative) return fromNative;
        const fromTerzi = (c.denominazioneTerzi || '').trim();
        if (fromTerzi) return fromTerzi;
        // Ultimo fallback: ragione sociale salvata nel campo `nome`.
        return (c.nome || '').trim();
    }
    return `${c.nome || ''} ${c.surname || ''}`.trim();
};

export const getClientRagioneSociale = (c: any): string =>
    (c?.ragioneSociale || c?.denominazioneTerzi || '').trim();

export const getClientPartitaIva = (c: any): string =>
    (c?.partitaIva || c?.pivaOrCfTerzi || '').trim();

export const getClientCodiceSdi = (c: any): string =>
    (c?.codiceSdi || c?.codiceSdiTerzi || '').trim();

export const getClientPec = (c: any): string =>
    (c?.pec || c?.emailTerzi || '').trim();

// Sede legale dell'azienda: campi indirizzo nativi, con fallback sui campi
// "per terzi" per i record vecchi.
export const getClientSedeLegale = (c: any): {
    indirizzo: string;
    cap: string;
    citta: string;
    provincia: string;
} => ({
    indirizzo: (c?.indirizzo || c?.sedeTerzi || '').trim(),
    cap: (c?.cap || c?.capTerzi || '').trim(),
    citta: (c?.citta || c?.comuneTerzi || '').trim(),
    provincia: (c?.provincia || c?.provinciaTerzi || '').trim(),
});

// Per i clienti Azienda restituisce il nome del referente fisico
// ("firmatario per conto di X srl"), per i Privati restituisce stringa vuota.
export const getClientReferente = (c: any): string => {
    if (!c || c.tipo !== 'Azienda') return '';
    return `${c.nome || ''} ${c.surname || ''}`.trim();
};

export const isClienteAzienda = (c: any): boolean =>
    c?.tipo === 'Azienda';

// Etichetta del cliente per liste, ricerche e dropdown.
// Azienda: "Ragione Sociale (Referente)" o uno dei due; Privato: "Nome Cognome".
export const getClientFullLabel = (c: any): string => {
    if (!c) return '';
    if (isClienteAzienda(c)) {
        const referente = getClientReferente(c);
        // Niente fallback su `nome` qui: nelle aziende `nome` è il referente,
        // e si otterrebbe "Nome Cognome (Nome)".
        const ragioneSociale = getClientRagioneSociale(c);
        if (ragioneSociale && referente) return `${ragioneSociale} (${referente})`;
        if (ragioneSociale) return ragioneSociale;
        if (referente) return referente;
        // Solo qui: fallback per la ragione sociale salvata nel campo `nome`.
        return getClientDisplayName(c);
    }
    return getClientDisplayName(c);
};

// Email/telefono del soggetto da mostrare in liste e schede:
// - Privato → email/telefono nativi (= persona)
// - Azienda → email/telefono del referente (campi *Referente);
//             fallback ai nativi e (per email) alla PEC per i record vecchi.
export const getClientEmail = (c: any): string => {
    if (!c) return '';
    if (c.tipo === 'Azienda') {
        return (c.emailReferente || c.email || c.pec || '').trim();
    }
    return (c.email || '').trim();
};

export const getClientTelefono = (c: any): string => {
    if (!c) return '';
    if (c.tipo === 'Azienda') {
        return (c.telefonoReferente || c.telefono || '').trim();
    }
    return (c.telefono || '').trim();
};

// Residenza del soggetto (persona fisica): per Privato i campi nativi,
// per Azienda i *Referente con fallback ai nativi.
// La sede legale dell'azienda è separata e si legge con getClientSedeLegale.
export const getClientResidenza = (c: any): {
    indirizzo: string;
    cap: string;
    citta: string;
    provincia: string;
} => {
    if (!c) return { indirizzo: '', cap: '', citta: '', provincia: '' };
    if (c.tipo === 'Azienda') {
        return {
            indirizzo: (c.indirizzoReferente || c.indirizzo || '').trim(),
            cap:       (c.capReferente       || c.cap       || '').trim(),
            citta:     (c.cittaReferente     || c.citta     || '').trim(),
            provincia: (c.provinciaReferente || c.provincia || '').trim(),
        };
    }
    return {
        indirizzo: (c.indirizzo || '').trim(),
        cap:       (c.cap       || '').trim(),
        citta:     (c.citta     || '').trim(),
        provincia: (c.provincia || '').trim(),
    };
};

// Privato che agisce in nome e per conto proprio per scelta esplicita: i dati del terzo
// eventualmente rimasti (copia del cliente nel mandato, vecchi salvataggi) non vanno usati.
// Vale la copia nel mandato se indica la scelta, altrimenti il cliente attuale.
export const isPrivatoPerContoProprio = (cliente: any, live?: any): boolean =>
    (cliente?.perContoDiTerzi ?? live?.perContoDiTerzi) === false;

// Clienti della sede selezionata, come nella lista Clienti: con una sede scelta solo i suoi,
// più quelli senza sede per il titolare (così li può correggere). Senza sede numerica, tutti.
export const clientiDellaSede = <C extends { sedeId?: any; sede?: any }>(clienti: C[], sedeId: unknown, titolare: boolean): C[] => {
    if (typeof sedeId !== 'number') return clienti;
    return clienti.filter(c => {
        const sede = c.sedeId ?? c.sede?.id;
        return sede == null || sede === '' ? titolare : String(sede) === String(sedeId);
    });
};
